import { useQuery } from '@tanstack/react-query';
import { authInterceptor } from '../lib/auth-interceptor';

export interface Service {
  id: number;
  contractor_id: number;
  service_type: string;
  name: string;
  unit: string;
  base_price: string;
  labor_rate: string;
  labor_method: string;
  created_at: string;
  updated_at: string;
}

export interface ServiceType {
  id: number;
  value: string;
  label: string;
  unit: string;
  type: 'linear' | 'area' | 'count' | 'roofing' | 'volume';
  category?: string;
  description?: string;
  price: number;
  laborRate: number;
  laborMethod: string;
}

// Convert database service to frontend service type
const convertServiceToType = (service: Service): ServiceType => {
  try {
    const price = parseFloat(service.base_price) || 0;
    const laborRate = parseFloat(service.labor_rate) || 0;
    
    return {
      id: service.id, // Preserve the original ID
      value: service.service_type,
      label: service.name,
      unit: service.unit,
      type: getTypeFromUnit(service.unit),
      category: getCategoryFromServiceType(service.service_type),
      description: `${service.name} - $${price.toFixed(2)}/${service.unit}`,
      price,
      laborRate,
      laborMethod: service.labor_method
    };
  } catch (error) {
    console.error('Error converting service:', service, error);
    // Return a safe default
    return {
      id: service.id || 0, // Preserve ID even in error case
      value: service.service_type || 'unknown',
      label: service.name || 'Unknown Service',
      unit: service.unit || 'unit',
      type: 'count',
      category: 'Other',
      description: 'Error converting service',
      price: 0,
      laborRate: 0,
      laborMethod: service.labor_method || 'by_unit'
    };
  }
};

// Helper function to determine type from unit
const getTypeFromUnit = (unit: string): 'linear' | 'area' | 'count' | 'roofing' | 'volume' => {
  if (!unit) return 'count';
  if (unit.includes('ft') && !unit.includes('sq')) return 'linear';
  if (unit.includes('sq')) return 'area';
  if (unit.includes('hour') || unit.includes('each') || unit.includes('unit')) return 'count';
  if (unit.includes('square')) return 'roofing';
  return 'count';
};

// Helper function to determine category from service type
const getCategoryFromServiceType = (serviceType: string): string => {
  if (!serviceType) return 'Other';
  const outdoor = ['fence', 'gate', 'deck', 'patio', 'landscaping'];
  const exterior = ['roof', 'roofing', 'gutters', 'siding', 'windows'];
  const interior = ['flooring', 'painting', 'drywall'];
  const systems = ['electrical', 'plumbing', 'hvac'];
  const concrete = ['concrete', 'masonry'];
  
  if (outdoor.includes(serviceType)) return 'Outdoor';
  if (exterior.includes(serviceType)) return 'Exterior';
  if (interior.includes(serviceType)) return 'Interior';
  if (systems.includes(serviceType)) return 'Systems';
  if (concrete.includes(serviceType)) return 'Concrete';
  return 'Other';
};

export function useServices() {
  const { data: rawServices = [], isLoading, error, refetch } = useQuery({
    queryKey: ['/api/direct/services'],
    queryFn: async () => {
      try {
        const headers = authInterceptor.addAuthHeaders();
        console.log('🔍 Services API headers:', headers);
        
        const response = await fetch('/api/direct/services', {
          headers
        });
        
        console.log('🔍 Services API response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('🔍 Raw services data:', data);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('❌ Error fetching services:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent frequent refetches
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on mount if data exists
    retry: 2, // Retry failed requests only 2 times
    retryDelay: 2000, // Wait 2 seconds between retries
  });

  // Convert database services to frontend service types
  const serviceTypes: ServiceType[] = rawServices.map(convertServiceToType);

  // Get services by category
  const getServicesByCategory = (category: string): ServiceType[] => {
    return serviceTypes.filter(service => service.category === category);
  };

  // Get all categories
  const getCategories = (): string[] => {
    return [...new Set(serviceTypes.map(service => service.category).filter(Boolean))];
  };

  // Get service by type
  const getServiceByType = (serviceType: string): ServiceType | undefined => {
    return serviceTypes.find(service => service.value === serviceType);
  };

  return {
    services: serviceTypes,
    rawServices: rawServices,
    isLoading,
    error,
    refetch,
    getServicesByCategory,
    getCategories,
    getServiceByType
  };
}

export default useServices;
